/**
 * Client-side filter: match a document against a MongoDB-style query (produced by convertFilterToMongoDB).
 * Used for tables whose API does not support a filter parameter (Freezed Masjids, Archives, End Users, Admins).
 */

const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let current = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
};

const matchesCondition = (doc: any, key: string, value: any): boolean => {
  const docVal = key.includes(".") ? getNestedValue(doc, key) : doc[key];

  if (value === null || typeof value !== "object") {
    return docVal === value;
  }

  if (value.$eq !== undefined) return docVal === value.$eq;
  if (value.$ne !== undefined) return docVal !== value.$ne;
  // Check date/number range before single operators so both $gte and $lte are applied
  if (value.$gte !== undefined && value.$lte !== undefined) {
    const d = docVal != null ? new Date(docVal).getTime() : NaN;
    const g = new Date(value.$gte).getTime();
    const l = new Date(value.$lte).getTime();
    return !isNaN(d) && d >= g && d <= l;
  }
  if (value.$gt !== undefined) return docVal != null && docVal > value.$gt;
  if (value.$gte !== undefined) return docVal != null && docVal >= value.$gte;
  if (value.$lt !== undefined) return docVal != null && docVal < value.$lt;
  if (value.$lte !== undefined) return docVal != null && docVal <= value.$lte;
  if (value.$in) return Array.isArray(value.$in) && value.$in.includes(docVal);
  if (value.$nin) return Array.isArray(value.$nin) && !value.$nin.includes(docVal);

  if (value.$regex !== undefined) {
    const str = docVal != null ? String(docVal) : "";
    const regex = new RegExp(value.$regex, value.$options || "");
    return regex.test(str);
  }
  if (value.$not && value.$not.$regex) {
    const str = docVal != null ? String(docVal) : "";
    const regex = new RegExp(value.$not.$regex, value.$not.$options || "");
    return !regex.test(str);
  }

  return false;
};

const matchesQuery = (doc: any, query: any): boolean => {
  if (!query || typeof query !== "object") return true;

  if (query.$and && Array.isArray(query.$and)) {
    return query.$and.every((clause: any) => matchesQuery(doc, clause));
  }
  if (query.$or && Array.isArray(query.$or)) {
    return query.$or.some((clause: any) => matchesQuery(doc, clause));
  }

  for (const key of Object.keys(query)) {
    if (key === "$and" || key === "$or") continue;
    if (!matchesCondition(doc, key, query[key])) return false;
  }
  return true;
};

/**
 * Filter an array of documents by a MongoDB query JSON string (from convertFilterToMongoDB).
 * Returns the filtered array. If filterJson is null/empty, returns the original array.
 */
export function filterListByMongoQuery<T>(list: T[], filterJson: string | null): T[] {
  if (!list?.length) return list;
  if (!filterJson || !filterJson.trim()) return list;
  try {
    const query = JSON.parse(filterJson);
    return list.filter((doc) => matchesQuery(doc, query));
  } catch {
    return list;
  }
}
